import { getApp } from "firebase/app";
import { httpsCallable, getFunctions } from "firebase/functions";
import {
  Timestamp,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../../lib/firebase/config";
import { invalidateReadingPpm } from "../../features/focus/lib/readingPpmCache";

export type FocusSessionStatus = "running" | "rewarded" | "cancelled";

/** Como a sessão foi iniciada (espelho de functions/src/shared/focusReward.ts). */
export type FocusSessionKind = "pomodoro" | "free" | "pages";

/** Como a sessão foi recompensada — `pomodoro_partial` é o encerramento cedo. */
export type FocusRewardKind =
  | "pomodoro_full"
  | "pomodoro_partial"
  | "free"
  | "pages";

export interface FocusSession {
  id: string;
  userId: string;
  bookId: string;
  status: FocusSessionStatus;
  targetDurationSec: number;
  /** Derivado no parse quando ausente (sessões pré-Frentes b/c). */
  sessionKind: FocusSessionKind;
  /** Meta de páginas — só em sessionKind "pages". */
  targetPages?: number;
  /**
   * Meta como o usuário a declarou, em pontos percentuais (livro Kindle).
   * Só EXIBIÇÃO — `targetPages` segue sendo o número canônico da sessão.
   */
  targetPercent?: number;
  startedAt: Timestamp;
  /** Hora do início (0-23) no fuso BR. Gravado pelo servidor em sessões criadas após 2026-05-11. */
  hourOfDayBR?: number;
  completedAt?: Timestamp;
  rewardedAt?: Timestamp;
  elapsedSec?: number;
  pagesRead?: number;
  /**
   * A fase 2 (registro das páginas) já rodou nesta sessão.
   *
   * ⚠️ **Ausente NÃO é `false`, é "ninguém declarou"** — decisão do BUG-170: a
   * fase 1 só grava o campo quando ela mesma registrou as páginas, porque um
   * `false` incondicional atropelaria a fase 2 concorrente. Todo leitor usa
   * `=== true`, e nenhuma query pode filtrar por `== false`: o Firestore não
   * casa documento sem o campo.
   */
  pagesRegistered?: boolean;
  /**
   * A pessoa viu o wrap-up e fechou SEM declarar (BUG-267) — a saída
   * explícita ou o X. É o que faz a recuperação parar de reabrir a sessão.
   * Ausente = "ninguém respondeu"; `=== true`, nunca coerção, como
   * `pagesRegistered`.
   */
  pagesDeclined?: boolean;
  /**
   * Moedas que a fase 1 pagou, lidas do doc.
   *
   * Só importa na sessão **recuperada**: ali não houve resposta de callable
   * nesta carga do app, e sem este campo o wrap-up perderia a pílula "já
   * creditadas" — que é justamente o que diz à pessoa que declarar as páginas
   * é opcional, no caso em que ela menos lembra da sessão.
   */
  rewardCoins?: number;
  /**
   * O XP de liga desta sessão espera as páginas (BUG-310). Mesmo papel do
   * `rewardCoins` na sessão RECUPERADA: sem resposta de callable nesta carga,
   * é o doc que diz ao fecho que há XP esperando. `=== true`, nunca coerção.
   */
  xpPending?: boolean;
  /** Minutos que o claim pagou — o potencial do XP pendente é isto × 6. */
  claimRewardMin?: number;
  ppm?: number;
  /**
   * Instante da pausa VIGENTE. `null`/ausente = rodando.
   *
   * ⚠️ Pausa não é `status` — o doc segue `"running"` pausado (ver
   * docs/systems/focus-pause.md §3). Quem responde "está pausada?" é este campo.
   */
  pausedAt?: Timestamp | null;
  /** Acumulado das pausas já retomadas, em segundos. Ausente = 0. */
  pausedTotalSec?: number;
  /** Quantas vezes a sessão foi pausada. Auditoria. */
  pauseCount?: number;
  updatedAt?: Timestamp;
  /**
   * Foto do LIVRO no instante do start (BUG-215) — título, capa e formato,
   * gravados pelo servidor a partir do que o client mandou. É o que permite à
   * recuperação mostrar o livro certo ANTES de a estante carregar (e mesmo
   * que ele não esteja nela). Ausentes em sessão anterior à fatia.
   */
  bookTitle?: string;
  bookCoverUrl?: string;
  bookFormat?: "physical" | "kindle";
}

/**
 * O que o client sabe do livro na hora de iniciar — viaja no `startFocusSession`
 * e vira `bookTitle`/`bookCoverUrl`/`bookFormat` no doc. Só exibição, nunca
 * economia: o servidor sanitiza e descarta o que não estiver bem formado, sem
 * recusar a sessão por causa disso.
 */
export interface FocusBookSnapshotInput {
  title?: string | null;
  coverUrl?: string | null;
  format?: "physical" | "kindle" | null;
}

export interface StartFocusSessionResponse {
  sessionId: string;
  status: FocusSessionStatus;
  startedAtMs: number;
  targetDurationSec: number;
  sessionKind?: FocusSessionKind;
  targetPages?: number | null;
  targetPercent?: number | null;
  bookId: string;
  /** Só quando o servidor devolveu uma sessão JÁ existente, que pode estar pausada. */
  pausedAtMs?: number | null;
  pausedTotalSec?: number;
}

/**
 * O que o usuário declarou no wrap-up — na UNIDADE do formato do livro.
 * Físico: páginas lidas na sessão. Kindle: a posição ABSOLUTA (1-100) que o
 * aparelho mostra no rodapé — o servidor deriva o delta e converte pra páginas
 * UMA vez, com o `totalPages` efetivo (mesma fronteira da meta em % do start).
 * Os dois campos são mutuamente exclusivos.
 */
export interface FocusWrapUpDeclaration {
  pagesRead?: number;
  endPercent?: number;
  /**
   * A pessoa fechou o wrap-up sem declarar (BUG-267). Vira `declinePages` no
   * payload — e só quando não há declaração: quem manda páginas não recusa.
   */
  declined?: boolean;
}

export interface CompleteFocusSessionResponse {
  sessionId: string;
  status: FocusSessionStatus;
  elapsedSec: number;
  /** Páginas DECLARADAS pelo usuário. */
  pagesRead: number;
  /**
   * Páginas efetivamente creditadas pós-teto de plausibilidade.
   *
   * **Só vem quando `sessionKind === "pages"`.** Ausente nos outros modos por
   * decisão de contrato (fix 2): o conceito não se aplica, e devolver `0`
   * envenenava todo consumidor que usasse `crediblePages ?? pagesRead` — o
   * nullish coalescing não trata `0` como ausente. Trate a AUSÊNCIA, nunca o
   * zero. Ver `docs/systems/estudo-economia-foco.md` §1.5 (A1).
   */
  crediblePages?: number;
  /** Eco da posição em % declarada (só quando o wrap-up declarou em %). */
  endPercent?: number;
  /**
   * Pontos percentuais efetivamente creditados — o que o fecho mostra na
   * unidade do leitor Kindle, sem re-converter no client. Vem junto de
   * `endPercent`; pode ser menor que o delta declarado se o teto de
   * plausibilidade wall-clock cortou.
   */
  crediblePercentPoints?: number;
  ppm: number | null;
  sessionKind?: FocusSessionKind;
  rewardKind?: FocusRewardKind | null;
  rewarded: boolean;
  /** Encerrada por abandono (ficou aberta tempo demais) — sem recompensa. */
  abandoned?: boolean;
  /** Moedas NOMINAIS creditadas (o +50% do PRO entra por cima, na carteira). */
  coinsAwarded: number;
  /**
   * Valor persistido na sessão pela fase 1 — a resposta idempotente da fase 2
   * devolve `coinsAwarded: 0` (nada foi re-creditado) e é ESTE campo que diz
   * quanto a sessão pagou. Ver `mergeRewardResult` no useFocusTimer.
   */
  rewardCoins?: number;
  balance: number;
  transactionId: string | null;
  /** Minutos que renderam XP (já cortados pelo teto diário de minutos). */
  rewardMin?: number;
  /** O teto diário de MINUTOS cortou o XP desta sessão. */
  minutesCapReached?: boolean;
  /** O teto diário de MOEDAS cortou a recompensa desta sessão. */
  coinCapReached?: boolean;
  /** Teto diário de moedas de foco, pra UI poder dizer o número. */
  dailyCoinCap?: number;
  /** Esta sessão foi a responsável por acender a chama do dia. */
  streakIncremented?: boolean;
  streakAfter?: number;
  /** A fase 2 (páginas) já rodou nesta sessão — ecoado pelo servidor. */
  pagesRegistered?: boolean;
  /** A recusa (BUG-267) está gravada no doc — ecoado pelo servidor. */
  pagesDeclined?: boolean;
  /**
   * O servidor conseguiu somar as páginas ao exemplar da estante.
   *
   * `false` com páginas declaradas e `applyToProgress` ligado significa que o
   * `bookId` da sessão não resolveu nenhum `user_books` (livro fora da estante,
   * ou id que a resolução flexível não casou) — a liga e a trilha contaram, o
   * livro não andou. Sempre existiu na resposta; o client é que o ignorava, e
   * o wrap-up dizia "somar ao progresso" para um progresso que não mudava
   * (BUG-215).
   */
  progressApplied?: boolean;
  markedAsRead?: boolean;
  /**
   * XP de liga no regime PENDENTE (BUG-310, `docs/systems/focus-xp-pending.md`).
   * Ausente = servidor antigo ou interruptor desligado: o fecho anuncia
   * `rewardMin × FOCUS_XP_PER_MIN` como sempre. `pending` = o XP espera as
   * páginas (e o servidor OMITE `rewardMin`); `paid` = entrou, valendo
   * `leagueXpPaid`; `expired` = a semana da liga virou antes das páginas.
   */
  leagueXpStatus?: "paid" | "pending" | "expired";
  /** Só com `paid`, nunca `0` solto. */
  leagueXpPaid?: number;
  /** Qual lado do "vale o maior" pagou. */
  leagueXpVia?: "minutes" | "pages";
  /** Só com `pending`: os minutos × 6 que esperam as páginas. */
  leagueXpPotential?: number;
}

/**
 * O espelho de MAX_FOCUS_SESSION_ELAPSED_SEC MUDOU DE CASA (21/08): mora em
 * `features/focus/lib/focusElapsed.ts`, o módulo PURO que já guarda o espelho
 * de pausa — a Live Activity precisa dele sem arrastar firebase pro grafo de
 * import. Re-exportado daqui para os call sites históricos; segue havendo UMA
 * cópia no client.
 */
export { MAX_FOCUS_SESSION_ELAPSED_SEC } from "../../features/focus/lib/focusElapsed";
import { MAX_COLLECT_WAIT_SEC, pickNewestByStart } from "../../features/focus/lib/focusElapsed";

export interface CancelFocusSessionResponse {
  sessionId: string;
  status: "cancelled" | "running";
  cancelled: boolean;
  /**
   * `collectable` (BUG-226): o servidor RECUSOU cancelar porque o alvo da
   * sessão já venceu — há moedas, XP e ofensiva a coletar, e cancelar os
   * apagaria. A sessão segue `running` e o caminho é o wrap-up.
   */
  reason?: "collectable";
}

const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as
  | string
  | undefined;

function getFunctionsInstance() {
  const app = getApp();
  return functionsRegion && functionsRegion.trim().length > 0
    ? getFunctions(app, functionsRegion.trim())
    : getFunctions(app);
}

function assertSignedIn(userId: string) {
  if (!auth.currentUser || auth.currentUser.uid !== userId) {
    throw new Error("User must be signed in");
  }
}

function getFocusSessionsRef(userId: string) {
  return collection(db, "user_stats", userId, "focus_sessions");
}

function toFocusSession(id: string, raw: Record<string, unknown>): FocusSession | null {
  if (
    typeof raw.userId !== "string" ||
    typeof raw.bookId !== "string" ||
    typeof raw.status !== "string" ||
    !(raw.startedAt instanceof Timestamp) ||
    typeof raw.targetDurationSec !== "number"
  ) {
    return null;
  }
  const status = raw.status;
  if (status !== "running" && status !== "rewarded" && status !== "cancelled") return null;
  const rawKind = raw.sessionKind;
  // Back-compat: sessão criada antes do deploy das Frentes b/c não tem
  // sessionKind gravado — deriva do contrato antigo (0 = livre).
  const sessionKind: FocusSessionKind =
    rawKind === "pomodoro" || rawKind === "free" || rawKind === "pages"
      ? rawKind
      : raw.targetDurationSec === 0
        ? "free"
        : "pomodoro";
  return {
    id,
    userId: raw.userId,
    bookId: raw.bookId,
    status,
    startedAt: raw.startedAt,
    targetDurationSec: raw.targetDurationSec,
    sessionKind,
    targetPages: typeof raw.targetPages === "number" ? raw.targetPages : undefined,
  targetPercent: typeof raw.targetPercent === "number" ? raw.targetPercent : undefined,
    completedAt: raw.completedAt instanceof Timestamp ? raw.completedAt : undefined,
    rewardedAt: raw.rewardedAt instanceof Timestamp ? raw.rewardedAt : undefined,
    elapsedSec: typeof raw.elapsedSec === "number" ? raw.elapsedSec : undefined,
    pagesRead: typeof raw.pagesRead === "number" ? raw.pagesRead : undefined,
    // `=== true` e não coerção: ausente é "ninguém declarou" (ver o campo).
    pagesRegistered: raw.pagesRegistered === true,
    pagesDeclined: raw.pagesDeclined === true,
    rewardCoins: typeof raw.rewardCoins === "number" ? raw.rewardCoins : undefined,
    xpPending: raw.xpPending === true,
    claimRewardMin: typeof raw.claimRewardMin === "number" ? raw.claimRewardMin : undefined,
    ppm: typeof raw.ppm === "number" ? raw.ppm : undefined,
    // Sem estes três, uma sessão PAUSADA recuperada depois de fechar e reabrir
    // o app volta a tickar na tela como se estivesse rodando. O servidor
    // continua certo (ele recalcula do zero no encerramento) — quem mente é a
    // tela, até o próximo round-trip. Não é opcional.
    pausedAt: raw.pausedAt instanceof Timestamp ? raw.pausedAt : null,
    pausedTotalSec: typeof raw.pausedTotalSec === "number" ? raw.pausedTotalSec : 0,
    pauseCount: typeof raw.pauseCount === "number" ? raw.pauseCount : 0,
    updatedAt: raw.updatedAt instanceof Timestamp ? raw.updatedAt : undefined,
    // A foto do livro (BUG-215). Sem estes três a recuperação depende da
    // estante em memória — que no cold start ainda está vazia quando o claim
    // é tomado — e o drawer nasce "Sessão de leitura", sem capa.
    ...(typeof raw.bookTitle === "string" && raw.bookTitle.trim() ?
      { bookTitle: raw.bookTitle } :
      {}),
    ...(typeof raw.bookCoverUrl === "string" && raw.bookCoverUrl.trim() ?
      { bookCoverUrl: raw.bookCoverUrl } :
      {}),
    ...(raw.bookFormat === "kindle" || raw.bookFormat === "physical" ?
      { bookFormat: raw.bookFormat } :
      {}),
  };
}

export async function startFocusSession(
  userId: string,
  bookId: string,
  durationSec = 25 * 60,
  targetPages?: number,
  /**
   * Meta em pontos percentuais (livro Kindle). O servidor converte pra páginas
   * com o `totalPages` efetivo — o client NUNCA converte, porque não tem o
   * denominador certo (pode vir de outra edição do mesmo work).
   */
  targetPercent?: number,
  /**
   * Foto do livro para a recuperação (BUG-215). Opcional: sessão sem livro e
   * chamadores antigos seguem funcionando, e um servidor anterior à fatia
   * simplesmente ignora as chaves (a callable não é `.strict()`).
   */
  bookSnapshot?: FocusBookSnapshotInput,
  /**
   * REPLAY de um start que morreu em voo (BUG-275): o instante em que a
   * pessoa iniciou de verdade. O servidor recua o `startedAt` até ele, com
   * teto (`MAX_FOCUS_START_BACKDATE_SEC`). Só entra no payload quando é um
   * número finito — o encoder do `httpsCallable` manda `null` para `undefined`,
   * e o schema é `.strict()`.
   */
  resumeFromMs?: number,
): Promise<StartFocusSessionResponse> {
  assertSignedIn(userId);
  const start = httpsCallable<
    {
      bookId: string;
      durationSec?: number;
      targetPages?: number;
      targetPercent?: number;
      bookTitle?: string;
      bookCoverUrl?: string;
      bookFormat?: "physical" | "kindle";
      resumeFromMs?: number;
    },
    StartFocusSessionResponse
  >(getFunctionsInstance(), "startFocusSession");
  const snapshot = focusBookSnapshotPayload(bookSnapshot);
  const replay =
    typeof resumeFromMs === "number" && Number.isFinite(resumeFromMs) && resumeFromMs > 0 ?
      { resumeFromMs: Math.floor(resumeFromMs) } :
      {};
  // Sessão por páginas NÃO manda durationSec: o servidor rejeita a combinação
  // de targetPages/targetPercent com alvo cronometrado (mutuamente exclusivos).
  const res = await start(
    targetPercent != null ?
      { bookId, targetPercent, ...snapshot, ...replay } :
      targetPages != null ?
        { bookId, targetPages, ...snapshot, ...replay } :
        { bookId, durationSec, ...snapshot, ...replay },
  );
  return res.data;
}

/**
 * A resposta do `startFocusSession` como `FocusSession` (BUG-275).
 *
 * Existe para o REPLAY da intenção de iniciar no boot: o provider não relê o
 * doc que a callable acabou de criar (seria um round-trip a mais no instante
 * em que o Firestore está reconectando — a mesma razão do BUG-228), ele adota
 * a resposta. Os campos que o doc tem e a resposta não (`hourOfDayBR`,
 * `rewardCoins`…) ficam ausentes, o que é o mesmo que uma sessão anterior às
 * fatias que os criaram — todo leitor já os trata como opcionais.
 */
export function focusSessionFromStartAck(
  userId: string,
  ack: StartFocusSessionResponse,
  bookSnapshot?: FocusBookSnapshotInput | null,
): FocusSession {
  const title = bookSnapshot?.title?.trim();
  const coverUrl = bookSnapshot?.coverUrl?.trim();
  const format = bookSnapshot?.format;
  return {
    id: ack.sessionId,
    userId,
    bookId: ack.bookId,
    status: ack.status,
    targetDurationSec: ack.targetDurationSec,
    sessionKind:
      ack.sessionKind ?? (ack.targetPages != null ? "pages" : ack.targetDurationSec === 0 ? "free" : "pomodoro"),
    targetPages: ack.targetPages ?? undefined,
    targetPercent: ack.targetPercent ?? undefined,
    startedAt: Timestamp.fromMillis(ack.startedAtMs),
    pausedAt: ack.pausedAtMs != null ? Timestamp.fromMillis(ack.pausedAtMs) : null,
    pausedTotalSec: ack.pausedTotalSec ?? 0,
    ...(title ? { bookTitle: title } : {}),
    ...(coverUrl ? { bookCoverUrl: coverUrl } : {}),
    ...(format === "physical" || format === "kindle" ? { bookFormat: format } : {}),
  };
}

/** Entrada do registro do push de conclusão da Live Activity (blueprint `focus-live-activity-push.md`). */
export interface RegisterFocusLiveActivityPushInput {
  sessionId: string;
  activityId: string;
  /** Token do APNs da activity, em hex. */
  liveActivityToken: string;
  /** Registration token do FCM do aparelho — o alvo da mensagem. `null` = sem alcance. */
  fcmToken: string | null;
  /** O vencimento que a ILHA conta, em ms (`computeDueMs`). `null` = pausada / sem alvo. */
  dueMs: number | null;
  /** O `data` da activity, para o servidor reproduzir o `content-state` inteiro. */
  data: Record<string, string | number | boolean>;
}

export interface RegisterFocusLiveActivityPushResponse {
  scheduled: boolean;
  /** `already_sent` (BUG-232): mesmo vencimento, push já enviado — nada a agendar. */
  skipReason?: "no_fcm_token" | "no_due" | "already_sent";
}

/**
 * Registra o token da Live Activity para o servidor avisar a conclusão no
 * vencimento. Mostrador, nunca economia: quem chama trata falha como warn.
 */
export async function registerFocusLiveActivityPush(
  userId: string,
  input: RegisterFocusLiveActivityPushInput,
): Promise<RegisterFocusLiveActivityPushResponse> {
  assertSignedIn(userId);
  const register = httpsCallable<RegisterFocusLiveActivityPushInput, RegisterFocusLiveActivityPushResponse>(
    getFunctionsInstance(),
    "registerFocusLiveActivityPush",
  );
  const res = await register(input);
  return res.data;
}

/**
 * As chaves da foto do livro que vão no payload do start — só as que têm
 * valor. Pura e exportada porque é a FORMA do contrato com o servidor
 * (`sanitizeFocusBookSnapshot`, do outro lado, lê exatamente estes nomes).
 */
export function focusBookSnapshotPayload(
  snapshot: FocusBookSnapshotInput | undefined,
): { bookTitle?: string; bookCoverUrl?: string; bookFormat?: "physical" | "kindle" } {
  if (!snapshot) return {};
  const title = snapshot.title?.trim();
  const coverUrl = snapshot.coverUrl?.trim();
  return {
    ...(title ? { bookTitle: title } : {}),
    ...(coverUrl ? { bookCoverUrl: coverUrl } : {}),
    ...(snapshot.format === "kindle" || snapshot.format === "physical" ?
      { bookFormat: snapshot.format } :
      {}),
  };
}

/**
 * Encerra a sessão e credita a recompensa (FASE 1) — e, se `pagesRead` vier,
 * registra as páginas (FASE 2) na mesma chamada.
 *
 * Decisão ③ (28/07): a recompensa passou a ser creditada quando a sessão
 * ENCERRA, no servidor, não quando o usuário aperta um botão no wrap-up. Chame
 * SEM `pagesRead` assim que o timer parar; chame de novo COM `pagesRead` se o
 * usuário declarar páginas. A segunda chamada é idempotente e nunca re-credita
 * moeda — o servidor guarda `pagesRegistered` no doc da sessão.
 *
 * É isso que faz o X do wrap-up parar de descartar recompensa conquistada
 * (estudo §3, G1): quando ele aparece, o crédito já aconteceu.
 */
export async function completeFocusSession(
  userId: string,
  sessionId: string,
  declaration?: FocusWrapUpDeclaration,
  applyToProgress = true,
): Promise<CompleteFocusSessionResponse> {
  assertSignedIn(userId);
  const complete = httpsCallable<
    {
      sessionId: string;
      pagesRead?: number;
      endPercent?: number;
      applyToProgress?: boolean;
      declinePages?: boolean;
    },
    CompleteFocusSessionResponse
  >(getFunctionsInstance(), "completeFocusSession");
  const pagesRead =
    declaration?.pagesRead != null && declaration.pagesRead > 0 ?
      declaration.pagesRead :
      undefined;
  const endPercent =
    declaration?.endPercent != null && declaration.endPercent > 0 ?
      declaration.endPercent :
      undefined;
  // A recusa (BUG-267) só viaja SEM declaração: antes dela, "não consegui
  // contar" mandava `{ sessionId }` — o payload da fase 1, byte a byte — e o
  // servidor não tinha como saber que a pessoa tinha respondido. ⚠️ Chave nova
  // num schema `.strict()`: functions ANTES do hosting (invariante 59).
  const declinePages =
    declaration?.declined === true && pagesRead == null && endPercent == null;
  const res = await complete(
    pagesRead == null && endPercent == null ?
      declinePages ? { sessionId, declinePages: true } : { sessionId } :
      {
        sessionId,
        ...(pagesRead != null ? { pagesRead } : {}),
        ...(endPercent != null ? { endPercent } : {}),
        applyToProgress,
      },
  );
  // O ritmo (páginas/minuto) acabou de mudar — sem isto a estimativa de
  // conclusão ficaria presa no valor antigo até o TTL do cache expirar, e o
  // usuário que declarou páginas pela PRIMEIRA vez não veria a estimativa
  // aparecer (é exatamente o que o wrap-up acabou de prometer a ele).
  invalidateReadingPpm(userId);
  return res.data;
}

/**
 * ⚠️ `incrementBookProgressFromFocusSession` foi APOSENTADA em 28/07 (decisão
 * ③ + K3). O progresso do livro passou a ser escrito pelo SERVIDOR, dentro de
 * `completeFocusSession` → `functions/src/focus/registerPages.ts`.
 *
 * Dois motivos, nesta ordem:
 *  1. **Unidade.** A versão client somava páginas cruas no `progress`, que em
 *     livro Kindle é um percentual — o livro pulava de 30% pra 50% com uma
 *     sessão de 20 páginas, nunca fechava sozinho, e quando o campo passava de
 *     100 o `updateBookProgress` trancava o usuário fora dele (estudo §2, A5).
 *     Corrigir no client exigiria um SEGUNDO espelho da conversão, e espelho
 *     que diverge foi exatamente o que criou o problema.
 *  2. **Atomicidade.** Com liga, trilha e `totalPagesRead` já sendo escritos no
 *     servidor, deixar só a estante no client abria uma janela (aba fechada,
 *     offline) em que a liga contava e a estante não — e os dois nunca
 *     reconciliavam.
 *
 * `findUserBookByIdFlexible` também saiu: o equivalente server-side vive em
 * `registerPages.ts` e usa a mesma resolução canônica de `reviews/functions.ts`.
 */

/** Resposta de `pauseFocusSession` / `resumeFocusSession`. */
export interface FocusPauseResponse {
  sessionId: string;
  status: FocusSessionStatus;
  /** A sessão está congelada AGORA. No resume expirado continua `true`. */
  paused: boolean;
  pausedAtMs?: number | null;
  pausedTotalSec: number;
  pauseCount?: number;
  /** Elapsed efetivo autoritativo — o client usa pra reconciliar o mostrador. */
  effectiveElapsedSec: number;
  maxPauseSec?: number;
  /** Só no resume. */
  resumed?: boolean;
  /**
   * Só no resume: a pausa passou do teto e a sessão NÃO foi destravada. Não é
   * erro — é o desfecho que leva ao wrap-up com o elapsed congelado.
   */
  expired?: boolean;
}

/**
 * Congela o relógio da sessão no servidor.
 *
 * Idempotente: chamar duas vezes não move o ponto de congelamento nem credita
 * tempo. Erro aqui não é fatal para o usuário — o servidor recalcula tudo no
 * encerramento —, mas o client precisa saber pra não afirmar na tela uma pausa
 * que o servidor não registrou.
 */
export async function pauseFocusSession(
  userId: string,
  sessionId: string,
): Promise<FocusPauseResponse> {
  assertSignedIn(userId);
  const pause = httpsCallable<{ sessionId: string }, FocusPauseResponse>(
    getFunctionsInstance(),
    "pauseFocusSession",
  );
  const res = await pause({ sessionId });
  return res.data;
}

/**
 * Destrava o relógio — ou devolve `expired: true` se a pausa passou do teto,
 * caso em que a sessão SEGUE congelada e o caminho é o wrap-up.
 */
export async function resumeFocusSession(
  userId: string,
  sessionId: string,
): Promise<FocusPauseResponse> {
  assertSignedIn(userId);
  const resume = httpsCallable<{ sessionId: string }, FocusPauseResponse>(
    getFunctionsInstance(),
    "resumeFocusSession",
  );
  const res = await resume({ sessionId });
  return res.data;
}

export async function cancelFocusSession(
  userId: string,
  sessionId: string,
): Promise<CancelFocusSessionResponse> {
  assertSignedIn(userId);
  const cancel = httpsCallable<
    { sessionId: string },
    CancelFocusSessionResponse
  >(getFunctionsInstance(), "cancelFocusSession");
  const res = await cancel({ sessionId });
  return res.data;
}

/**
 * Quantas sessões recompensadas recentes olhar atrás de páginas pendentes.
 *
 * Baixo de propósito: só interessa a última, e a janela de coleta (12 h) já
 * limita quantas podem estar pendentes de verdade. O número existe como folga
 * para o caso de a pessoa ter encerrado duas sessões seguidas.
 */
const PENDING_PAGES_SCAN = 3;

/**
 * A sessão que o app precisa retomar no boot.
 *
 * São DUAS perguntas, e a ordem entre elas é o desenho: uma sessão **rodando**
 * é a resposta primária e a mais barata, e o caminho comum paga exatamente o
 * que pagava antes desta função crescer. Só quando não há nenhuma é que
 * perguntamos a segunda — se ficou uma sessão **já recompensada** cujas páginas
 * nunca foram declaradas.
 *
 * ⚠️ **A segunda pergunta não pode ser feita por igualdade.** O instinto é
 * `where("pagesRegistered","==",false)`, e ele acha ZERO documentos: a fase 1
 * só grava o campo quando ela mesma registrou as páginas (BUG-170), então o
 * estado "ninguém declarou" é o campo **ausente** — e o Firestore não casa
 * documento sem o campo. Daí a query ordenar por `rewardedAt` (gravado
 * incondicionalmente pela fase 1) e o filtro morar em memória.
 *
 * ⚠️ E ela **precisa** de janela. Medido em produção, 20,9% dos pomodoros
 * recompensados nunca receberam página: sem recorte de tempo, "a sessão com
 * páginas pendentes" seria o passado inteiro da pessoa. A janela é a mesma da
 * coleta (`MAX_COLLECT_WAIT_SEC`) e pelo mesmo motivo — `registerFocusSessionPages`
 * credita no `dayKey` de HOJE, então declarar páginas de anteontem as poria na
 * trilha e na ofensiva de hoje.
 *
 * Custo: 1 leitura no caminho comum; +3 quando não há sessão rodando.
 */
export async function getActiveFocusSession(userId: string): Promise<FocusSession | null> {
  if (!auth.currentUser || auth.currentUser.uid !== userId) return null;
  const ref = getFocusSessionsRef(userId);

  // `limit(3)` e a mais NOVA em memória (BUG-231): mais de uma `running` era
  // possível antes da trava do start, e `limit(1)` sem ordem devolvia uma
  // arbitrária — a recuperação podia abrir uma sessão diferente da que o
  // start acabara de devolver. Ordenar na query exigiria índice composto.
  const runningSnap = await getDocs(
    query(ref, where("status", "==", "running"), limit(3)),
  );
  if (!runningSnap.empty) {
    const candidatas = runningSnap.docs.map((docSnap) => {
      const session = toFocusSession(docSnap.id, docSnap.data() as Record<string, unknown>);
      return { session, startedAtMs: session?.startedAt.toMillis() ?? null };
    });
    const escolhida = pickNewestByStart(candidatas.filter((c) => c.session != null));
    return escolhida?.session ?? null;
  }

  // `orderBy("rewardedAt")` também é o filtro de status: sessão rodando e
  // sessão cancelada não têm o campo, então saem da query por construção — sem
  // custar um `where`, que aqui exigiria índice composto.
  const settledSnap = await getDocs(
    query(ref, orderBy("rewardedAt", "desc"), limit(PENDING_PAGES_SCAN)),
  );
  const cutoffMs = Date.now() - MAX_COLLECT_WAIT_SEC * 1000;
  for (const docSnap of settledSnap.docs) {
    const session = toFocusSession(docSnap.id, docSnap.data() as Record<string, unknown>);
    if (!session || session.status !== "rewarded") continue;
    if (session.pagesRegistered === true) continue;
    // A pessoa VIU o wrap-up e fechou sem declarar (BUG-267): perguntar de novo
    // a cada boot por 12 h era o defeito. O resgate F2 é para a sessão que
    // nunca foi vista.
    if (session.pagesDeclined === true) continue;
    const rewardedMs = session.rewardedAt?.toMillis();
    if (typeof rewardedMs !== "number" || rewardedMs < cutoffMs) continue;
    return session;
  }
  return null;
}

export async function getCompletedFocusSessions(userId: string, max = 200): Promise<FocusSession[]> {
  if (!auth.currentUser || auth.currentUser.uid !== userId) return [];
  const q = query(
    getFocusSessionsRef(userId),
    orderBy("updatedAt", "desc"),
    limit(Math.max(1, Math.min(500, max))),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => toFocusSession(d.id, d.data() as Record<string, unknown>))
    .filter((session): session is FocusSession => session != null && session.status === "rewarded");
}
